import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  getArticles, getEvents, getCommunity, getFeaturedPet, getDailyDeal, getBusinesses, getContests,
} from "../lib/api";
import { fmtEventDate, fmtEventTime } from "../lib/eventFormat";
import SectionSponsor from "../components/SectionSponsor";
import LoyaltyBadge from "../components/LoyaltyBadge";
import CoastalMasthead from "../components/CoastalMasthead";
import {
  ChevronDown, CalendarDays, MapPin, Star, Flame, Heart, Trophy, MessageSquareQuote,
  Ticket, Store, ArrowRight, Clock, Film, Newspaper, Megaphone, Menu, X, Sparkles,
} from "lucide-react";

const CITIES = ["Fernandina Beach", "Amelia Island", "Yulee", "Callahan", "Hilliard"];
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const FALLBACK = "https://images.unsplash.com/photo-1595432849291-3036ea5e5475?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const onErr = (e) => { if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK; };

export default function NassauToday() {
  const [articles, setArticles] = useState([]);
  const [events, setEvents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [scores, setScores] = useState([]);
  const [pet, setPet] = useState(null);
  const [deal, setDeal] = useState(null);
  const [biz, setBiz] = useState([]);
  const [contests, setContests] = useState([]);

  useEffect(() => {
    getArticles({ limit: 100 }).then(setArticles).catch(() => {});
    getEvents().then(setEvents).catch(() => {});
    getCommunity("voice").then(setVoices).catch(() => {});
    getCommunity("score").then(setScores).catch(() => {});
    getFeaturedPet().then(setPet).catch(() => {});
    getDailyDeal().then(setDeal).catch(() => {});
    getBusinesses().then(setBiz).catch(() => {});
    getContests().then(setContests).catch(() => {});
  }, []);

  const lead = articles.find((a) => a.featured) || articles[0];
  const byCat = (c) => articles.filter((a) => a.category === c);

  return (
    <div className="portal">
      <PortalHeader />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8">
        <CoastalMasthead />
        <HeroBento lead={lead} events={events} deal={deal} />
        <ParticipatoryCTA />
        <MarketplaceHub pet={pet} deal={deal} biz={biz} contests={contests} />
        <div className="grid gap-6 lg:grid-cols-3">
          <LocalVoices voices={voices} />
          <Scoreboard scores={scores} />
        </div>
        <Entertainment items={byCat("entertainment")} />
        <LocalNewsByCity articles={articles} />
      </main>
      <PortalFooter />
    </div>
  );
}

/* ---------------- Header ---------------- */
export function PortalHeader() {
  const [cityOpen, setCityOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setCityOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const links = [
    { label: "Local Voices", to: "/community" },
    { label: "Events", to: "/events" },
    { label: "Marketplace", to: "/marketplace" },
    { label: "Loyalty Club", to: "/loyalty", highlight: true },
    { label: "Contests", to: "/contests" },
    { label: "Entertainment", to: "/#entertainment" },
    { label: "Galleries", to: "/galleries" },
    { label: "e-Edition", to: "/e-edition" },
  ];
  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 shadow-sm backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3">
        <Link to="/" data-testid="nassau-today-brand-logo" className="flex items-center gap-2">
          <img src="/brand/nassau-life-mark.png" alt="" data-testid="portal-topbar-logo-mark" className="h-8 w-auto" />
          <span className="font-portal text-lg font-extrabold tracking-tight text-[var(--p-ink)] sm:text-xl">Nassau <span className="text-[var(--p-teal)]">Life</span></span>
        </Link>

        <nav className="hidden items-center gap-0.5 whitespace-nowrap xl:flex">
          <div className="relative" ref={ref}>
            <button data-testid="local-news-city-dropdown" onClick={() => setCityOpen((v) => !v)} className="flex items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
              Local News <ChevronDown size={15} className={`transition-transform ${cityOpen ? "rotate-180" : ""}`} />
            </button>
            {cityOpen && (
              <div className="absolute left-0 top-full mt-1 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                <a href="#local-news" onClick={() => setCityOpen(false)} className="block px-4 py-2 text-sm font-semibold text-[var(--p-teal)] hover:bg-slate-50">All Nassau County</a>
                <div className="my-1 border-t border-slate-100" />
                {CITIES.map((c) => (
                  <a key={c} href="#local-news" data-testid={`city-option-${slug(c)}`} onClick={() => setCityOpen(false)} className="flex items-center justify-between px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
                    {c} <span className="portal-chip rounded bg-slate-100 px-1.5 py-0.5 text-[9px] text-slate-400">Soon</span>
                  </a>
                ))}
              </div>
            )}
          </div>
          {links.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`portal-nav-${slug(l.label)}`} className={`rounded-lg px-2.5 py-2 text-sm font-semibold hover:bg-slate-100 ${l.highlight ? "inline-flex items-center gap-1 text-[var(--p-clay)]" : "text-slate-700"}`}>{l.highlight && <Sparkles size={13} />}{l.label}</Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/admin/login" className="hidden whitespace-nowrap text-sm font-medium text-slate-600 hover:text-slate-900 md:block">Sign In</Link>
          <Link to="/subscribe" data-testid="portal-subscribe-button" className="lift rounded-full bg-[var(--p-teal)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[var(--p-teal-bright)]">Subscribe</Link>
          <button onClick={() => setMobile((v) => !v)} data-testid="portal-mobile-menu-button" aria-label="Open menu" className="rounded-lg p-2 text-slate-700 hover:bg-slate-100 xl:hidden">{mobile ? <X size={20} /> : <Menu size={20} />}</button>
        </div>
      </div>
      {mobile && (
        <div className="border-t border-slate-200 bg-white px-4 py-3 xl:hidden">
          <a href="/#local-news" onClick={() => setMobile(false)} className="block py-2 text-sm font-semibold text-slate-700">Local News</a>
          {links.map((l) => <Link key={l.label} to={l.to} onClick={() => setMobile(false)} className="block py-2 text-sm font-semibold text-slate-700">{l.label}</Link>)}
        </div>
      )}
    </header>
  );
}

/* ---------------- Hero bento ---------------- */
function HeroBento({ lead, events, deal }) {
  return (
    <section className="mb-8 grid gap-5 md:grid-cols-12">
      <div className="md:col-span-7">
        {lead ? (
          <Link to={`/article/${lead.slug}`} className="portal-card group block h-full overflow-hidden">
            <div className="h-64 overflow-hidden sm:h-80">
              <img src={lead.image || FALLBACK} onError={onErr} alt={lead.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-5 sm:p-6">
              <span className="portal-chip text-[var(--p-teal-bright)]">Top Local Story</span>
              <h2 className="font-portal mt-2 text-2xl font-extrabold leading-tight text-[var(--p-ink)] sm:text-3xl">{lead.title}</h2>
              <p className="mt-2 line-clamp-2 text-sm text-slate-600">{lead.excerpt}</p>
              <p className="mt-3 text-xs font-semibold text-slate-400">By {lead.author_name}</p>
            </div>
          </Link>
        ) : <div className="portal-card h-full animate-pulse bg-slate-100" />}
      </div>

      <div className="md:col-span-5">
        <CommunityCalendar events={events} />
      </div>

      {deal && (
        <Link to="/loyalty" data-testid="portal-daily-deal-banner" className="portal-card lift md:col-span-12 flex flex-col items-start justify-between gap-3 overflow-hidden bg-gradient-to-r from-[#EE9B00] to-[#CA6702] p-5 text-white sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20"><Flame size={22} /></span>
            <div>
              <p className="portal-chip text-white/80">Today's Daily Deal</p>
              <p className="font-portal text-lg font-extrabold">{deal.title} — {deal.business}</p>
            </div>
          </div>
          <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-[var(--p-clay)]">{deal.discount} · Grab it →</span>
        </Link>
      )}
    </section>
  );
}

function CommunityCalendar({ events }) {
  return (
    <div data-testid="portal-community-calendar" className="portal-card flex h-full flex-col overflow-hidden">
      <div className="flex items-center justify-between gap-3 bg-[var(--p-ink)] px-5 py-3 text-white">
        <span className="font-portal flex items-center gap-2 text-sm font-bold"><CalendarDays size={18} className="text-[var(--p-amber)]" /> Community Calendar</span>
        <div className="flex items-center gap-2">
          <SectionSponsor section="events" />
          <Link to="/events" className="text-xs font-semibold text-[var(--p-amber)] hover:underline">Full →</Link>
        </div>
      </div>
      <div className="flex-1 divide-y divide-slate-100">
        {events.slice(0, 4).map((e) => {
          const d = new Date((e.start || "").length <= 10 ? e.start + "T00:00" : e.start);
          const mon = isNaN(d) ? "" : d.toLocaleDateString("en-US", { month: "short" });
          const day = isNaN(d) ? "" : d.getDate();
          return (
            <Link key={e.id} to="/events" data-testid={`portal-event-card-${e.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
              <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-[var(--p-teal)]/10 text-[var(--p-teal)]">
                <span className="portal-chip text-[9px] leading-none">{mon}</span>
                <span className="font-portal text-lg font-extrabold leading-none">{day}</span>
              </div>
              <div className="min-w-0">
                <p className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">{e.sponsored && <Star size={10} className="fill-[var(--p-amber)] text-[var(--p-amber)]" />}{fmtEventTime(e.start) || "All day"} · {e.category}</p>
                <p className="font-portal truncate text-sm font-bold text-[var(--p-ink)]">{e.title}</p>
                {e.location && <p className="flex items-center gap-1 truncate text-[11px] text-slate-500"><MapPin size={11} /> {e.location}</p>}
              </div>
            </Link>
          );
        })}
        {events.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-400">No upcoming events yet.</p>}
      </div>
    </div>
  );
}

/* ---------------- Participatory CTA ---------------- */
function ParticipatoryCTA() {
  const items = [
    { to: "/events", icon: CalendarDays, label: "Submit an event" },
    { to: "/community", icon: MessageSquareQuote, label: "Share your voice" },
    { to: "/admin/login", icon: Megaphone, label: "Become a contributor" },
  ];
  return (
    <section data-testid="portal-submit-news-cta-button" className="mb-8 flex flex-col items-start justify-between gap-6 rounded-2xl bg-gradient-to-r from-[#005F73] to-[#0A9396] p-7 text-white shadow-lg md:flex-row md:items-center">
      <div>
        <h3 className="font-portal text-2xl font-extrabold">This is your community's story — help write it.</h3>
        <p className="mt-1 max-w-xl text-sm text-white/85">Nassau County Today is powered by neighbors like you. Share local news, post an event, or become a community contributor.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((i) => (
          <Link key={i.label} to={i.to} className="lift flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-bold text-[var(--p-teal)] hover:bg-[var(--p-amber)] hover:text-[var(--p-ink)]">
            <i.icon size={16} /> {i.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Marketplace hub ---------------- */
function MarketplaceHub({ pet, deal, biz, contests }) {
  const featuredBiz = [...biz].sort((a, b) => (!!b.loyalty - !!a.loyalty) || ((b.tier === "premium") - (a.tier === "premium"))).slice(0, 3);
  const contest = contests[0];
  return (
    <section data-testid="portal-marketplace-hub" className="mb-8">
      <SectionTitle icon={Store} eyebrow="Shop & support local" title="Marketplace & Community Hub" to="/marketplace" />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {pet && (
          <Link to="/pets" data-testid="portal-pet-of-week-card" className="portal-card group overflow-hidden">
            <div className="h-36 overflow-hidden"><img src={pet.image || FALLBACK} onError={onErr} alt={pet.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" /></div>
            <div className="p-4">
              <span className="portal-chip flex items-center gap-1 text-[var(--p-clay)]"><Heart size={11} /> Pet of the Week</span>
              <p className="font-portal mt-1 text-lg font-extrabold text-[var(--p-ink)]">{pet.name}</p>
              <p className="text-xs text-slate-500">{pet.breed} · {pet.age}</p>
            </div>
          </Link>
        )}
        <Link to="/loyalty" data-testid="portal-daily-deal-card" className="portal-card group flex flex-col justify-between overflow-hidden bg-[var(--p-ink)] p-5 text-white">
          <span className="portal-chip flex items-center gap-1 text-[var(--p-amber)]"><Flame size={12} /> Daily Deal · Loyalty Club</span>
          {deal ? (
            <div className="mt-3"><p className="font-portal text-lg font-extrabold">{deal.title}</p><p className="text-sm text-white/70">{deal.business}</p>
              <p className="font-portal mt-2 text-2xl font-black text-[var(--p-amber)]">{deal.discount}</p></div>
          ) : <p className="mt-3 text-sm text-white/70">Check back for today's deal.</p>}
          <span className="mt-3 flex items-center gap-1 text-xs font-bold text-[var(--p-amber)]">See the deal <ArrowRight size={13} /></span>
        </Link>
        <Link to="/directory" className="portal-card group overflow-hidden p-5">
          <span className="portal-chip flex items-center gap-1 text-[var(--p-teal-bright)]"><Store size={12} /> Business Directory</span>
          <div className="mt-3 space-y-2">
            {featuredBiz.map((b) => (
              <div key={b.id || b.name} className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--p-teal)]/10 text-[var(--p-teal)]"><Store size={13} /></span>
                <span className="truncate text-sm font-semibold text-[var(--p-ink)]">{b.name}</span>
                {b.loyalty && <LoyaltyBadge label="Club" className="shrink-0" />}
                {b.tier === "premium" && <Star size={11} className="fill-[var(--p-amber)] text-[var(--p-amber)]" />}
              </div>
            ))}
            {featuredBiz.length === 0 && <p className="text-sm text-slate-400">Explore local businesses.</p>}
          </div>
          <span className="mt-3 flex items-center gap-1 text-xs font-bold text-[var(--p-teal)]">Browse directory <ArrowRight size={13} /></span>
        </Link>
        <Link to="/contests" data-testid="portal-contests-card" className="portal-card group flex flex-col justify-between overflow-hidden bg-gradient-to-br from-[#CA6702] to-[#EE9B00] p-5 text-white">
          <span className="portal-chip flex items-center gap-1 text-white/85"><Ticket size={12} /> Contests</span>
          <div className="mt-3">
            <p className="font-portal text-lg font-extrabold">{contest ? contest.title : "Enter to Win"}</p>
            <p className="mt-1 line-clamp-2 text-sm text-white/85">{contest ? contest.description : "Local giveaways and reader contests."}</p>
          </div>
          <span className="mt-3 flex items-center gap-1 text-xs font-bold">Enter now <ArrowRight size={13} /></span>
        </Link>
      </div>
    </section>
  );
}

/* ---------------- Local voices & scoreboard ---------------- */
function LocalVoices({ voices }) {
  return (
    <section data-testid="portal-local-voices-section" className="lg:col-span-2">
      <SectionTitle icon={MessageSquareQuote} eyebrow="From your neighbors" title="Local Voices" to="/community" />
      <div className="grid gap-5 sm:grid-cols-2">
        {voices.slice(0, 4).map((v) => (
          <Link key={v.id} to="/community" className="portal-card group overflow-hidden">
            {v.image && <div className="h-32 overflow-hidden"><img src={v.image} onError={onErr} alt={v.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
            <div className="p-4">
              <span className="portal-chip flex items-center gap-1 text-[var(--p-teal-bright)]"><Megaphone size={11} /> Community Voice</span>
              <p className="font-portal mt-1 text-base font-bold leading-snug text-[var(--p-ink)]">{v.title}</p>
              <p className="mt-1 line-clamp-2 text-sm text-slate-600">{(v.body || [])[0]}</p>
              <p className="mt-2 text-xs font-semibold text-slate-400">By {v.author_name}</p>
            </div>
          </Link>
        ))}
        {voices.length === 0 && <p className="text-sm text-slate-400">Community posts will appear here.</p>}
      </div>
    </section>
  );
}

function Scoreboard({ scores }) {
  return (
    <section data-testid="portal-scoreboard-widget">
      <SectionTitle icon={Trophy} eyebrow="High school sports" title="Scoreboard" to="/community" sectionKey="sports" />
      <div className="space-y-4">
        {scores.slice(0, 4).map((s) => (
          <div key={s.id} className="portal-card overflow-hidden">
            <div className="flex items-center justify-between bg-[var(--p-teal)] px-4 py-1.5 text-white">
              <span className="portal-chip flex items-center gap-1"><Trophy size={12} className="text-[var(--p-amber)]" /> {s.sport}</span>
              <span className="text-[10px] uppercase text-white/60">Coach report</span>
            </div>
            <div className="grid grid-cols-3 items-center gap-1 p-3 text-center">
              <div><p className="font-portal text-xs font-bold text-[var(--p-ink)]">{s.home_team}</p><p className="font-portal text-2xl font-black text-[var(--p-teal)]">{s.home_score}</p></div>
              <span className="text-[10px] font-bold uppercase text-slate-400">vs</span>
              <div><p className="font-portal text-xs font-bold text-[var(--p-ink)]">{s.away_team}</p><p className="font-portal text-2xl font-black text-slate-400">{s.away_score}</p></div>
            </div>
          </div>
        ))}
        {scores.length === 0 && <p className="text-sm text-slate-400">No game reports yet.</p>}
      </div>
    </section>
  );
}

/* ---------------- Entertainment ---------------- */
function Entertainment({ items }) {
  if (items.length === 0) return null;
  return (
    <section id="entertainment" data-testid="portal-entertainment-section" className="mt-8">
      <SectionTitle icon={Film} eyebrow="Arts · Music · Food & Nightlife" title="Entertainment & Culture" to="/section/entertainment" sectionKey="entertainment" />
      <div className="grid gap-5 md:grid-cols-3">
        {items.slice(0, 3).map((a) => (
          <Link key={a.slug} to={`/article/${a.slug}`} className="portal-card group overflow-hidden">
            <div className="h-44 overflow-hidden"><img src={a.image || FALLBACK} onError={onErr} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
            <div className="p-4">
              <span className="portal-chip text-[var(--p-clay)]">Entertainment</span>
              <p className="font-portal mt-1 text-lg font-bold leading-snug text-[var(--p-ink)]">{a.title}</p>
              <p className="mt-1 line-clamp-2 text-sm text-slate-600">{a.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Local news by city ---------------- */
function LocalNewsByCity({ articles }) {
  const [city, setCity] = useState("All Nassau County");
  const news = articles.filter((a) => a.category === "news");
  const tagged = city === "All Nassau County" ? news : news.filter((a) => (a.city || "").toLowerCase() === city.toLowerCase());
  const shown = tagged.length ? tagged : news;
  const noneForCity = city !== "All Nassau County" && tagged.length === 0;
  return (
    <section id="local-news" className="mt-8">
      <SectionTitle icon={Newspaper} eyebrow="Hyper-local coverage" title="Local News by City" to="/section/news" sectionKey="news" />
      <div className="mb-5 flex flex-wrap gap-2">
        {["All Nassau County", ...CITIES].map((c) => (
          <button key={c} data-testid={`city-tab-${slug(c)}`} onClick={() => setCity(c)} className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${city === c ? "bg-[var(--p-teal)] text-white" : "border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>{c}</button>
        ))}
      </div>
      {noneForCity && <p className="mb-4 rounded-lg bg-[var(--p-teal)]/5 px-4 py-2 text-sm text-[var(--p-teal)]">City-specific feeds for <b>{city}</b> are coming soon — showing county-wide news for now.</p>}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {shown.slice(0, 6).map((a) => (
          <Link key={a.slug} to={`/article/${a.slug}`} className="portal-card group overflow-hidden">
            <div className="h-40 overflow-hidden"><img src={a.image || FALLBACK} onError={onErr} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
            <div className="p-4">
              <span className="portal-chip text-[var(--p-teal-bright)]">Local News</span>
              <p className="font-portal mt-1 text-base font-bold leading-snug text-[var(--p-ink)]">{a.title}</p>
              <p className="mt-1 flex items-center gap-2 text-xs text-slate-400"><span>{a.author_name}</span><span className="flex items-center gap-1"><Clock size={11} /> {a.read_time} min</span></p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Shared bits ---------------- */
function SectionTitle({ icon: Icon, eyebrow, title, to, sectionKey }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--p-teal)]/10 text-[var(--p-teal)]"><Icon size={20} /></span>
        <div>
          <p className="portal-chip text-[var(--p-teal-bright)]">{eyebrow}</p>
          <h2 className="font-portal text-2xl font-extrabold tracking-tight text-[var(--p-ink)] sm:text-3xl">{title}</h2>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {sectionKey && <SectionSponsor section={sectionKey} />}
        {to && <Link to={to} className="text-sm font-bold text-[var(--p-teal)] hover:underline">View all →</Link>}
      </div>
    </div>
  );
}

export function PortalFooter() {
  return (
    <footer className="mt-12 bg-[var(--p-ink)] text-white/70">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row">
        <div>
          <p className="font-portal text-lg font-extrabold text-white">Nassau <span className="text-[var(--p-amber)]">Life</span></p>
          <p className="text-xs">Powered by the News-Leader</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
          <Link to="/community" className="hover:text-white">Local Voices</Link>
          <Link to="/events" className="hover:text-white">Events</Link>
          <Link to="/marketplace" className="hover:text-white">Marketplace</Link>
          <Link to="/classic" data-testid="footer-classic-link" className="rounded-lg border border-white/20 px-3 py-1 text-xs hover:bg-white/10">News-Leader Classic Edition</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-xs text-white/40">© {new Date().getFullYear()} Nassau Life · Powered by the News-Leader</div>
    </footer>
  );
}
